import React from 'react';
import { Row } from 'react-table';
import { Loading, Paper } from '@brighterion/br-ui';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import useGetList from 'Hooks/query-hooks/list/useGetList';
import SplitViewHeader from 'Components/Table/SplitView/Header';
import SplitView from 'Components/Table/SplitView';
import GlobalRuleDetails from 'Components/GlobalRuleDetails';
import { UseSplitViewForm } from 'Components/Table/hooks/useSplitViewForm';
import SplitRevisionHistory from 'Components/SplitRevisionHistory';
import SplitRuleStatement from 'Components/SplitRuleStatement';
import MerchantOverridePaper from './MerchantOverridePaper';
import styles from './SplitViewContent.css';

interface Props extends UseSplitViewForm<GlobalRuleSplitViewFormState> {
    globalRules: GlobalRulesData;
    dataTestId: string;
    rows: Row<Rule>[];
    selectedRow: Row<Rule>;
    readOnly: boolean;
    toggleRowSelected(id: string, toggle: boolean): void;
    deploymentVersion?: number;
    merchantId?: string;
    revision: number;
    mode?: string;
}

const SplitViewContent = ({
    globalRules,
    dataTestId,
    selectedRow,
    rows,
    readOnly,
    toggleRowSelected,
    splitViewFormState,
    setInitialFormState,
    setIsOpen,
    setRowId,
    deploymentVersion,
    merchantId,
    revision,
    mode
}: Props) => {
    // RevisionHistory and current rule may have list changes or list rules
    // So we have to retrieve lists to find corresponding list names
    const listAPI = useGetList();
    const { t } = useTranslation();
    const { isOpen } = splitViewFormState;
    const closeSplitView = () => setRowId(null);
    const thisGlobalRule = globalRules.rules.find((grule) => grule.id === (selectedRow.original as Rule).id);
    const { values: selectedRowValues, original } = selectedRow;
    const showMerchantPaper = !(readOnly && original.inherit === 'ACQUIRER');
    const ruleIsActive = isOpen || !!(!selectedRowValues.override && thisGlobalRule?.parameters.active);

    // FIXME: what happens when global rule can't be find (error)?
    if (!thisGlobalRule) {
        return <div>Global Rule could not be found</div>;
    }

    if (listAPI.isError) return <>{t('Error Fetching', { fieldName: 'Lists' })}</>;
    if (listAPI.isLoading || listAPI.isFetching)
        return (
            <div className={styles.spinner}>
                <Loading />
            </div>
        );

    return (
        <>
            <SplitViewHeader dataTestId={dataTestId}>
                <SplitViewHeader.Title dataTestId={`${dataTestId}-title`} title={t('Merchant-Specific Rule Details')} />
                <SplitViewHeader.Close dataTestId={`${dataTestId}-close`} closeSplitView={closeSplitView} />
            </SplitViewHeader>
            <SplitView.Navigation
                dataTestId='splitview-nav'
                selectedRow={selectedRow}
                rows={rows}
                toggleRowSelected={toggleRowSelected}
            />
            <Paper className={styles.contentPanel}>
                {/* TODO: fix this active dispaly when Lawrence gets back to us */}
                <GlobalRuleDetails
                    ruleIsActive={ruleIsActive}
                    dataTestId={dataTestId}
                    globalRule={thisGlobalRule as Rule}
                />
            </Paper>
            {showMerchantPaper && (
                <MerchantOverridePaper
                    rule={original as Rule}
                    key={selectedRow?.id}
                    paperClassName={clsx(styles.contentPanel, styles.marginTop)}
                    dataTestId={dataTestId}
                    readOnly={readOnly}
                    setInitialFormState={setInitialFormState}
                    splitViewFormState={splitViewFormState}
                    setIsOpen={setIsOpen}
                    revision={revision}
                    selectedRowId={selectedRow.id}
                    merchantId={merchantId || ''}
                    mode={mode}
                />
            )}

            <SplitRuleStatement
                outputDescription={original?.outputDescription}
                ruleInputs={original?.inputs}
                parameters={original?.parameters}
                lists={original?.lists}
                listData={listAPI.data?.result}
                conclusionValues={original._formatted?.conclusionValues}
                expression={original?.expression}
            />

            <SplitRevisionHistory
                merchantId={merchantId}
                deploymentVersion={deploymentVersion}
                level='MERCHANT'
                key={original.id}
                ruleId={original?.id}
                listData={listAPI.data?.result}
            />
        </>
    );
};

export default SplitViewContent;
